import {
  Flex,
  HelperText,
  Label,
  RadioGroup,
  RadioTile,
  media,
  useMediaQuery,
} from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { FocusEvent, Ref, useEffect } from 'react';

import { FormikPersist } from '@components/FormikPersist';
import { ENERGY_DETAILS } from '@constants/formNames';
import { useEnergyContext } from '@features/Energy/Provider';
import {
  hasElectricityPrepayment,
  isEconomy7QuestionVisible,
} from '@features/Energy/utils';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import {
  EnergyFuelType,
  EnergyMeterType,
  EnergyPaymentOption,
  PredefinedConsumption,
} from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { EnergyDetailsFormValues } from '@src/types/energy';
import { BooleanSelection } from '@src/types/index';

import { CONSUMPTION_RADIO_GROUP_NAME } from '../constants';
import { EnergyRadioField } from './EnergyRadioField';
import { KnownUsageFields } from './KnownUsageFields';
import { PrepaymentFuelRadioField } from './PrepaymentFuelRadioField';
import './styles.scss';

interface FormProps {
  isLoading: boolean;
  onValidated(isValid: boolean): void;
  ref?: Ref<HTMLFormElement>;
}

const getRadioId = (value: string) =>
  `${CONSUMPTION_RADIO_GROUP_NAME}-${value}`;

const Form = ({ onValidated, isLoading, ref }: FormProps) => {
  const isMobile = useMediaQuery(media.below('tablet'));
  const { setDetailsFormChanged } = useEnergyContext();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldTouched,
    setFieldValue,
    isValid,
    touched,
    submitCount,
    dirty,
  } = useFormikContext<EnergyDetailsFormValues>();

  const getFieldProps = useFieldProps<EnergyDetailsFormValues>({
    errors,
    values,
    handleBlur,
    handleChange,
    touched,
    submitCount,
  });

  useEffect(() => {
    onValidated(isValid);
  }, [isValid, onValidated]);

  const showEconomy7Question = isEconomy7QuestionVisible(values);

  const isDualFuelPrepaymentSelected =
    values.hasPrepayment === BooleanSelection.Yes &&
    values.prepaymentFuelType === EnergyFuelType.DualFuel;

  // Economy 7 can't be combined with an electricity prepayment meter, so clear
  // a stale answer when the question is hidden
  useEffect(() => {
    if (
      hasElectricityPrepayment(values) &&
      values.meterType === EnergyMeterType.Economy_7
    ) {
      setFieldValue('meterType', EnergyMeterType.Standard);
    }
  }, [values, setFieldValue]);

  useEffect(() => {
    setDetailsFormChanged(dirty);
  }, [dirty, setDetailsFormChanged]);

  // Necessary to reset KnownUsageFields values
  const setTouchedOnFocus = (event: FocusEvent<HTMLElement>) => {
    const { id } = event.target;

    if (id) {
      setFieldTouched(id, true);
    }
  };

  return (
    <>
      <FormSync />
      <FormikPersist name={ENERGY_DETAILS} />

      <form onSubmit={handleSubmit} ref={ref}>
        <Flex spacing="xl" direction="column">
          <EnergyRadioField disabled={isLoading} />
          <RadioGroup
            {...getFieldProps(CONSUMPTION_RADIO_GROUP_NAME)}
            label={
              <>
                <Label fontWeight="semibold">How much energy do you use?</Label>
                <HelperText>
                  Figures for low, medium and high are based on national
                  averages. For a more accurate quote, use your actual usage
                  from a recent bill.
                </HelperText>
              </>
            }
            disabled={isLoading}
            onFocus={setTouchedOnFocus}
            contentWidth="100%"
            onValueChange={(val: string) =>
              setFieldValue(CONSUMPTION_RADIO_GROUP_NAME, val)
            }
          >
            <RadioTile
              value={PredefinedConsumption.Low}
              label="Low (1 bedroom)"
              helperText="Regularly empty on weekdays"
              data-testid="energy-low-usage-option"
              id={getRadioId(PredefinedConsumption.Low)}
            />
            <RadioTile
              value={PredefinedConsumption.Medium}
              label="Medium (2-3 bedrooms)"
              helperText="2-4 occupants, often someone at home"
              data-testid="energy-medium-usage-option"
              id={getRadioId(PredefinedConsumption.Medium)}
            />
            <RadioTile
              value={PredefinedConsumption.High}
              label="High (4+ bedrooms)"
              helperText="4+ occupants, need a lot of heating"
              data-testid="energy-high-usage-option"
              id={getRadioId(PredefinedConsumption.High)}
            />
            <RadioTile
              value={PredefinedConsumption.Custom}
              label="I want to enter my actual usage"
              helperText="You can find your annual usage on your energy bill"
              data-testid="energy-custom-usage-option"
              id={getRadioId(PredefinedConsumption.Custom)}
            />
          </RadioGroup>
          <KnownUsageFields isLoading={isLoading} />
          <RadioGroup
            {...getFieldProps('hasPrepayment')}
            label="Do you have a prepayment meter?"
            helperText="You have a prepayment meter if you top up your meter with credit, using a key or card."
            direction="row"
            disabled={isLoading}
            onValueChange={(val: string) => setFieldValue('hasPrepayment', val)}
          >
            <RadioTile
              value={BooleanSelection.Yes}
              label="Yes"
              data-testid="select-prepayment"
              id="select-prepayment-yes"
              flex={{ mobile: '1', tablet: '0' }}
            />
            <RadioTile
              value={BooleanSelection.No}
              label="No"
              id="select-prepayment-no"
              flex={{ mobile: '1', tablet: '0' }}
            />
          </RadioGroup>

          <PrepaymentFuelRadioField disabled={isLoading} />

          {showEconomy7Question && (
            <RadioGroup
              {...getFieldProps('meterType')}
              label="Do you have an Economy 7 meter?"
              helperText="You have an Economy 7 meter if your electricity meter has two readings rather than just one."
              direction="row"
              disabled={isLoading}
              color="grey1000"
              onValueChange={(val: string) => setFieldValue('meterType', val)}
            >
              <RadioTile
                value={EnergyMeterType.Economy_7}
                label="Yes"
                id="meter-type-economy"
                flex={{ mobile: '1', tablet: '0' }}
              />
              <RadioTile
                value={EnergyMeterType.Standard}
                label="No"
                id="meter-type-standard"
                flex={{ mobile: '1', tablet: '0' }}
              />
            </RadioGroup>
          )}

          {!isDualFuelPrepaymentSelected && (
            <RadioGroup
              {...getFieldProps('paymentOption')}
              label="How would you like to pay for your energy?"
              helperText="With a budget plan, you’ll make 12 equal monthly payments across the year through Direct Debit. If you pay monthly in arrears, you’ll pay for what you use each month, which means you’ll pay less during summer but more during the winter."
              direction={isMobile ? 'column' : 'row'}
              disabled={isLoading}
              onValueChange={(val: string) =>
                setFieldValue('paymentOption', val)
              }
              contentWidth={{ mobile: '100%', tablet: 'fit-content' }}
            >
              <RadioTile
                value={EnergyPaymentOption.BudgetPlan}
                label="Budget plan"
                id="payment-option-budget-plan"
                flex={{ mobile: '1', tablet: '0 0 auto' }}
              />
              <RadioTile
                value={EnergyPaymentOption.MonthlyInArrears}
                label="Monthly in arrears"
                id="payment-option-monthly-in-arrears"
                flex={{ mobile: '1', tablet: '0 0 auto' }}
              />
            </RadioGroup>
          )}
        </Flex>
      </form>
    </>
  );
};

export { Form };
